// Copyright (c) 2021 WiseTime. All rights reserved.

import { forkJoin, iif, noop, Observable, of, throwError } from "rxjs"
import { Action } from "redux"
import { catchError, filter, map, mergeMap, } from "rxjs/operators"
import Grpc from "grpc-web"
import * as Actions from "../actions"
import * as Slice from "../slice"
import { SiteId, UserDetails, UserId } from "../slice"
import { grpc } from "../../../app/rpc/executor"
import { User, WhoAmIRequest } from "../../../generated/wisetime/account/user/v4/user_pb"
import { never, RetryPolicy } from "grpc-web-rx"
import { membershipClient, userClient } from "../../../app/rpc/client"
import { refreshExpiredToken } from "../../../app/rpc/retry"
import { GetMembershipRequest, Membership } from "../../../generated/legebill/site/membership/v4/membership_pb"

type WhoAmIRpc = (retryPolicy: RetryPolicy) => Observable<User>
type GetMembershipRpc = (siteId: SiteId, userId: UserId) => Observable<Membership>
export const refreshExpiredTokenInBackgroundConfig = {
  maxRetries: 60,
  shouldRetry: (error: Grpc.RpcError): boolean => [
    Grpc.StatusCode.UNAUTHENTICATED, // 401
    Grpc.StatusCode.UNKNOWN, // 500
    Grpc.StatusCode.INTERNAL, // 500
    Grpc.StatusCode.UNAVAILABLE, // 503
    Grpc.StatusCode.DEADLINE_EXCEEDED, // 504
  ].includes(error.code),
  onSuccess: noop,
}

type ErrorNoSiteMemberships = "NO_SITE_MEMBERSHIPS"

/**
 * Fetches the user details from the WhoAmI endpoint plus user memberships from the GetMembership endpoint, and updates
 * the store.
 *
 * If the request is made to enforce user login then the retry action will attempt a token exchange before retrying
 * the source call, if this call fails then the user is redirected to the login page.
 */
export const fetchUserDetails = (
  whoAmIRpc: WhoAmIRpc,
  getMembershipRpc: GetMembershipRpc,
) => (action$: Observable<Action>): Observable<Action> =>
  action$.pipe(
    filter(Actions.fetchUserDetails.match),
    mergeMap(() =>
      whoAmIRpc(refreshExpiredToken(refreshExpiredTokenInBackgroundConfig))
        .pipe(
          mergeMap(ensureAtLeastOneMembership),
          mergeMap(user => fetchMembershipDetails(user, getMembershipRpc)
            .pipe(
              map(memberships => ({ user, memberships } as UserDetails))
            )
          ),
          map(Slice.setUserDetails),
          catchError(handleError),
        )
    )
  )

const whoAmIRpc = (retryPolicy: RetryPolicy): Observable<User> =>
  grpc(
    () => userClient.whoAmI(new WhoAmIRequest(), null),
    retryPolicy
  )

const getMembershipRpc = (siteId: SiteId, userId: UserId): Observable<Membership> =>
  grpc(
    () => membershipClient.getMembership(
      new GetMembershipRequest()
        .setUserId(userId)
        .setSiteId(siteId),
      null
    ),
    never
  )

const ensureAtLeastOneMembership = (user: User) =>
  iif(
    () => user.getTeamSitesList().length > 0,
    of(user),
    throwError(() => "NO_SITE_MEMBERSHIPS")
  )

const fetchMembershipDetails = (user: User, getMembershipRpc: GetMembershipRpc): Observable<Membership[]> =>
  forkJoin(
    user.getTeamSitesList()
      .map(site => getMembershipRpc(site.getId(), user.getId()))
  )

const errorNoMemberships = (e: unknown): e is ErrorNoSiteMemberships => {
  if (!e) {
    return false
  }

  const err = e as ErrorNoSiteMemberships
  return err === "NO_SITE_MEMBERSHIPS"
}

const handleError = (e: unknown): Observable<Action> => {
  if (errorNoMemberships(e)) {
    return of(Actions.redirectToAddTeam())
  }

  // token exchange and fetching of user (and/or membership) details failures are already handled (in retry.ts)
  // as well as initial page loading in the TimelinePage component
  return of(Actions.fetchUserDetailsFailed())
}

export default fetchUserDetails(whoAmIRpc, getMembershipRpc)
